import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { commentsCapture } from '@org/interfaces';
import * as moment from 'moment';

@Component({
  selector: 'lib-comment-thread',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './comment-thread.component.html',
  styleUrl: './comment-thread.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommentThreadComponent implements OnChanges, AfterViewChecked {
  @Input() comments: commentsCapture[] | commentsCapture | null = [];
  @Input() draft = '';
  @Input() loading = false;
  @Input() title = '';
  @Input() showHeader = false;
  @Input() disabled = false;
  @Input() composerId = 'comment-draft';

  @Output() draftChange = new EventEmitter<string>();
  @Output() send = new EventEmitter<void>();
  @Output() close = new EventEmitter<Event>();

  @ViewChild('threadScroll') threadScroll?: ElementRef<HTMLDivElement>;

  sortedComments: commentsCapture[] = [];

  private shouldStickToBottom = true;
  private pendingScroll = false;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['comments']) {
      const prevLen = this.sortedComments.length;
      this.sortedComments = this.normalizeAndSort(this.comments);
      const nextLen = this.sortedComments.length;
      if (changes['comments'].firstChange || nextLen !== prevLen) {
        this.pendingScroll = this.shouldStickToBottom || changes['comments'].firstChange;
      }
      this.cdr.markForCheck();
    }
  }

  ngAfterViewChecked(): void {
    if (this.pendingScroll) {
      this.scrollToBottom();
      this.pendingScroll = false;
    }
  }

  get canSend(): boolean {
    return !this.disabled && !this.loading && !!this.draft?.trim();
  }

  onDraftInput(value: string): void {
    this.draftChange.emit(value);
  }

  onSend(): void {
    if (!this.canSend) return;
    this.shouldStickToBottom = true;
    this.send.emit();
  }

  onComposerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      this.onSend();
    }
  }

  onThreadScroll(): void {
    const el = this.threadScroll?.nativeElement;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    this.shouldStickToBottom = distanceFromBottom < 48;
  }

  formatDate(date: Date | string | undefined): string {
    if (!date) return '';
    return moment(date).format('M/D/YY, h:mm A');
  }

  userName(comment: commentsCapture): string {
    return comment?.user?.name || 'Unknown User';
  }

  userInitial(comment: commentsCapture): string {
    const name = this.userName(comment).trim();
    return name ? name.charAt(0).toUpperCase() : 'U';
  }

  trackByComment(index: number, comment: commentsCapture): string {
    return `${index}-${comment?.timestamp}-${comment?.content}`;
  }

  private normalizeAndSort(
    comments: commentsCapture[] | commentsCapture | null | undefined
  ): commentsCapture[] {
    const list = Array.isArray(comments) ? comments : comments ? [comments] : [];
    return [...list].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  }

  private scrollToBottom(): void {
    const el = this.threadScroll?.nativeElement;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }
}
